import { useEffect, useState } from 'react';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { LoadingIndicator, Pagination } from 'components/common';
import {
  PageEmptyIndicator,
  PageSectionHeader,
  PageShowAllLink
} from 'components/page';
import { PathsAdminTile, PathsTile } from '.';

import type { GetPathsRes } from 'shared-types/responses';
import type { ViewProps } from 'types';

type Props = ViewProps & {
  onFilterDataReceived?: (
    data: GetPathsRes['pathsFilterOptions'] | null
  ) => void;
};

const PathsViewAllPaths = ({
  description,
  hideIfNoData = false,
  isHomeView = false,
  limitPerPage = 8,
  onFilterDataReceived,
  onNoData = null,
  queryParams = '',
  showAllLinkHref = null,
  title,
  titleShowCount = true
}: Props) => {
  const { messages, user } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, loading } = useApiData<GetPathsRes>({
    onDataUpdated: (data) => {
      // filter data is sent to parent
      if (onFilterDataReceived) {
        if (data?.pathsFilterOptions) {
          onFilterDataReceived(data?.pathsFilterOptions);
        } else {
          onFilterDataReceived(null);
        }
      }
    },
    url: `/admin/paths?limit=${limitPerPage}&page=${page}${queryParams}`,
    onlyIf: Boolean(user)
  });

  useEffect(() => {
    setPage(1);
  }, [queryParams]);

  useEffect(() => {
    const hasData = !loading && Boolean(data?.paths?.length);
    if (!loading && onNoData) onNoData(hasData);
  }, [data, loading]);

  return loading ? (
    <LoadingIndicator />
  ) : data?.pathsCount > 0 ? (
    <div className="content">
      <PageSectionHeader
        description={description}
        title={title || messages.pathsAllPaths}
        titleCount={titleShowCount ? data?.pathsCount || 0 : undefined}
      />

      <div className={`paths ${isHomeView ? 'paths-in-home-view' : ''}`}>
        {data.paths.map((path) => (
          <div className="path" key={path.id}>
            {path.owners?.some((owner) => owner.id === user?.id) ? (
              <PathsAdminTile path={path} />
            ) : (
              <PathsTile
                path={path}
                pathType={path.userIsEnrolled ? 'user' : 'available'}
                key={path.id}
              />
            )}
          </div>
        ))}
      </div>

      {data?.pages > 1 && !isHomeView ? (
        <Pagination
          currentPage={page}
          numberOfPages={data?.pages || 1}
          onPageChange={({ page }) => setPage(page)}
        />
      ) : showAllLinkHref && data?.pathsCount > data?.paths?.length ? (
        <PageShowAllLink
          href={showAllLinkHref}
          numberOfItems={data?.pathsCount}
        />
      ) : null}

      <style jsx>{`
        .paths {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 30px;
        }
        .path {
          width: 100%;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .paths-in-home-view .path {
            width: calc(50% - 15px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .path {
            width: calc(50% - 15px);
          }
          .paths-in-home-view .path {
            width: calc(33.333% - 20px);
          }
        }
      `}</style>
    </div>
  ) : hideIfNoData ? null : (
    <>
      <PageSectionHeader
        description={description}
        title={title || messages.pathsAllPaths}
        titleCount={titleShowCount ? 0 : undefined}
      />

      <PageEmptyIndicator
        icon="tasks"
        title={messages.pathsEmpty}
        description={messages.pathsEmpty}
      />
    </>
  );
};

export default PathsViewAllPaths;
